import { useState, useEffect } from 'react';
import { getNotificationPreferences, updateNotificationPreferences } from '../../../services/memberSettingsApi';
import type { NotificationPreferences as NotificationPreferencesData } from '../../../services/memberSettingsApi';
import { useAuth } from '../../../contexts/AuthContext';
import { isTempEmail } from '../../../utils/email';
import './NotificationPreferences.css';

const NotificationPreferences = () => {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<NotificationPreferencesData>({
    emailEnabled: true,
    weeklyDigest: false,
    newMatchAlert: true,
    wishContactedAlert: true,
    systemAnnouncements: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const isUserTempEmail = user ? isTempEmail(user.email) : false;

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    setIsLoading(true);
    try {
      const result = await getNotificationPreferences();
      if (result.success && result.data) {
        setPrefs(result.data);
      }
    } catch {
      console.error('Failed to load notification preferences');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage(null);

    try {
      const result = await updateNotificationPreferences(prefs);
      if (result.success) {
        setMessage({ type: 'success', text: '通知偏好已儲存' });
      } else {
        setMessage({ type: 'error', text: '儲存失敗，請稍後再試' });
      }
    } catch {
      setMessage({ type: 'error', text: '網路錯誤，請稍後再試' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="notification-preferences">
        <div className="notification-preferences__loading">載入中...</div>
      </div>
    );
  }

  return (
    <div className="notification-preferences">
      <h3 className="notification-preferences__title">通知偏好設定</h3>

      {message && (
        <div className={`notification-preferences__alert notification-preferences__alert--${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSave} className="notification-preferences__form">
        <h4 className="notification-preferences__section-title">電子郵件通知</h4>

        {isUserTempEmail && (
          <div className="notification-preferences__warning">
            ⚠️ 請先綁定電子郵件後才能啟用通知功能
          </div>
        )}

        <label className="notification-preferences__item">
          <input
            type="checkbox"
            checked={prefs.emailEnabled}
            onChange={(e) => setPrefs({ ...prefs, emailEnabled: e.target.checked })}
            disabled={isUserTempEmail}
          />
          <span>啟用電子郵件通知</span>
        </label>

        <label className="notification-preferences__item">
          <input
            type="checkbox"
            checked={prefs.newMatchAlert}
            onChange={(e) => setPrefs({ ...prefs, newMatchAlert: e.target.checked })}
            disabled={!prefs.emailEnabled}
          />
          <span>有新的廣告符合我的許願時發送通知</span>
        </label>

        <label className="notification-preferences__item">
          <input
            type="checkbox"
            checked={prefs.wishContactedAlert}
            onChange={(e) => setPrefs({ ...prefs, wishContactedAlert: e.target.checked })}
            disabled={!prefs.emailEnabled}
          />
          <span>有人聯繫我的許願時發送通知</span>
        </label>

        <label className="notification-preferences__item">
          <input
            type="checkbox"
            checked={prefs.weeklyDigest}
            onChange={(e) => setPrefs({ ...prefs, weeklyDigest: e.target.checked })}
            disabled={!prefs.emailEnabled}
          />
          <span>每週摘要（取代每日通知）</span>
        </label>

        <label className="notification-preferences__item">
          <input
            type="checkbox"
            checked={prefs.systemAnnouncements}
            onChange={(e) => setPrefs({ ...prefs, systemAnnouncements: e.target.checked })}
            disabled={!prefs.emailEnabled}
          />
          <span>接收系統公告通知</span>
        </label>

        <button
          type="submit"
          className="notification-preferences__save"
          disabled={isSaving}
        >
          {isSaving ? '儲存中...' : '儲存偏好設定'}
        </button>
      </form>
    </div>
  );
};

export default NotificationPreferences;
